import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Eye, Loader2, ArrowLeft, GraduationCap } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Badge } from '@/components/ui/badge';
import { BrutalCard } from '@/components/ui/BrutalCard';
import { BrutalButton } from '@/components/ui/BrutalButton';
import { BrutalInput } from '@/components/ui/BrutalInput';
import { BrutalTextarea } from '@/components/ui/BrutalTextarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { screeningQuestions, THRESHOLDS } from '@/data/screeningQuestions';
import AdminLayout from '@/components/admin/AdminLayout';

const ScreeningAdmin = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState<any>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    if (!isAdmin()) {
      toast({
        title: t('common.accessDenied'),
        description: t('common.adminOnly'),
        variant: 'error',
      });
      return;
    }
    loadApplications();
  }, []);

  const loadApplications = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('screening_applications')
        .select('*')
        .order('submitted_at', { ascending: false });

      if (error) throw error;
      setApplications(data || []);
    } catch (error: any) {
      console.error('Error loading applications:', error);
      toast({
        title: t('common.error'),
        description: error.message,
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const getScoreBadge = (score: number, badgeTier?: string | null) => {
    if (badgeTier === 'gold' || score >= THRESHOLDS.EXCELLENT) {
      return <Badge className="bg-yellow-500 text-dark border-[2px] border-dark">🥇 {score}</Badge>;
    }
    if (badgeTier === 'silver' || score >= THRESHOLDS.APPROVED) {
      return <Badge className="bg-gray-300 text-dark border-[2px] border-dark">🥈 {score}</Badge>;
    }
    if (score >= THRESHOLDS.PENDING_REVIEW) {
      return <Badge className="bg-warning text-dark border-[2px] border-dark">{score}</Badge>;
    }
    return <Badge className="bg-destructive text-white border-[2px] border-dark">{score}</Badge>;
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, any> = {
      pending: 'secondary',
      approved: 'default',
      rejected: 'destructive',
      needs_info: 'outline',
    };
    return <Badge variant={variants[status] || 'secondary'}>{t(`screening.statusBadge.${status}`)}</Badge>;
  };

  const handleViewDetails = (app: any) => {
    setSelectedApp(app);
    setAdminNotes(app.admin_notes || '');
    setRejectionReason(app.rejection_reason || '');
    setShowDialog(true);
  };

  const handleApprove = async (app: any) => {
    setProcessing(true);
    try {
      // Create profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .insert({
          user_id: app.user_id,
          business_name: t('screening.newProfessional'),
          slug: `professional-${Date.now()}`,
          is_verified: true,
          verified_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (profileError) throw profileError;

      // Update application
      const { error: updateError } = await supabase
        .from('screening_applications')
        .update({
          status: 'approved',
          reviewed_at: new Date().toISOString(),
          profile_id: profile.id,
          admin_notes: adminNotes,
        })
        .eq('id', app.id);

      if (updateError) throw updateError;

      // Send notification to user using unified system with email template
      await supabase.functions.invoke('create-system-notification', {
        body: {
          userId: app.user_id,
          type: 'screening_approved',
          titleIt: 'Profilo Approvato!',
          titleEn: 'Profile Approved!',
          titleEs: '¡Perfil Aprobado!',
          descriptionIt: 'Il tuo profilo professionale è stato approvato! Scegli ora il piano più adatto a te.',
          descriptionEn: 'Your professional profile has been approved! Choose the plan that suits you best.',
          descriptionEs: '¡Tu perfil profesional ha sido aprobado! Elige el plan que más te convenga.',
          actionUrl: '/pricing',
          ctaTextIt: 'Scegli Piano',
          ctaTextEn: 'Choose Plan',
          ctaTextEs: 'Elegir Plan',
          sendEmail: true,
          // Use email template from DB
          emailTemplateKey: 'profile_approved',
        },
      });

      toast({
        title: t('screening.approved'),
        description: t('screening.approvedDesc'),
      });

      setShowDialog(false);
      loadApplications();
    } catch (error: any) {
      console.error('Error approving application:', error);
      toast({
        title: t('common.error'),
        description: error.message,
        variant: 'error',
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (app: any) => {
    if (!rejectionReason.trim()) {
      toast({
        title: t('common.error'),
        description: t('screening.reasonRequired'),
        variant: 'error',
      });
      return;
    }

    setProcessing(true);
    try {
      const { error } = await supabase
        .from('screening_applications')
        .update({
          status: 'rejected',
          reviewed_at: new Date().toISOString(),
          admin_notes: adminNotes,
          rejection_reason: rejectionReason,
        })
        .eq('id', app.id);

      if (error) throw error;

      toast({
        title: t('screening.rejected'),
        description: t('screening.rejectedDesc'),
      });

      setShowDialog(false);
      loadApplications();
    } catch (error: any) {
      console.error('Error rejecting application:', error);
      toast({
        title: t('common.error'),
        description: error.message,
        variant: 'error',
      });
    } finally {
      setProcessing(false);
    }
  };

  // Filtra applicazioni
  const filteredApplications = applications.filter(app => {
    const matchesSearch = !searchTerm || 
      (app.user_email || app.user_id).toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || app.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Training needs chart data from q15 answers
  const trainingNeedsData = useMemo(() => {
    const counts: Record<string, number> = { a: 0, b: 0, c: 0, d: 0 };
    
    applications.forEach(app => {
      if (app.answers && typeof app.answers === 'object') {
        const q15Answer = app.answers.q15 as string;
        if (q15Answer && counts.hasOwnProperty(q15Answer)) {
          counts[q15Answer]++;
        }
      }
    });

    const total = counts.a + counts.b + counts.c + counts.d;

    return [
      { 
        name: 'Competenze tecniche', 
        fullName: 'Competenze tecniche avanzate', 
        value: counts.a, 
        percentage: total > 0 ? Math.round((counts.a / total) * 100) : 0,
        fill: 'hsl(var(--primary))' 
      },
      { 
        name: 'Comunicazione inclusiva', 
        fullName: 'Comunicazione inclusiva e diversità', 
        value: counts.b, 
        percentage: total > 0 ? Math.round((counts.b / total) * 100) : 0,
        fill: 'hsl(var(--success))' 
      },
      { 
        name: 'Marketing', 
        fullName: 'Marketing e acquisizione clienti', 
        value: counts.c, 
        percentage: total > 0 ? Math.round((counts.c / total) * 100) : 0,
        fill: 'hsl(var(--warning))' 
      },
      { 
        name: 'Gestione fiscale', 
        fullName: 'Gestione amministrativa e fiscale', 
        value: counts.d, 
        percentage: total > 0 ? Math.round((counts.d / total) * 100) : 0,
        fill: 'hsl(var(--destructive))' 
      },
    ];
  }, [applications]);

  const totalResponses = trainingNeedsData.reduce((sum, item) => sum + item.value, 0);

  if (!isAdmin()) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/dashboard">Dashboard</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t('screening.breadcrumb')}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={() => navigate("/dashboard")}
            className="p-2 hover:bg-accent transition-colors rounded-lg"
            aria-label={t('screening.backButton')}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-heading text-4xl font-bold flex items-center gap-3">
              <CheckCircle className="w-10 h-10 text-green-600" />
              {t('screening.title')}
            </h1>
            <p className="text-muted-foreground mt-2">
              {t('screening.applicationsCount', { filtered: filteredApplications.length, total: applications.length })}
            </p>
          </div>
        </div>

        {/* Training Needs Analytics Chart */}
        {totalResponses > 0 && (
          <BrutalCard className="mb-6">
            <h2 className="font-heading text-2xl font-bold mb-2 uppercase flex items-center gap-3">
              <GraduationCap className="w-8 h-8 text-primary" />
              Esigenze Formative dei Professionisti
            </h2>
            <p className="text-muted-foreground mb-6">
              Distribuzione delle risposte alla domanda: "In quale ambito vorresti maggiore formazione?" ({totalResponses} risposte)
            </p>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={trainingNeedsData} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeWidth={3} stroke="hsl(var(--dark))" />
                <XAxis 
                  type="number" 
                  stroke="hsl(var(--dark))"
                  style={{ fontFamily: 'var(--font-body)', fontWeight: 'bold' }}
                />
                <YAxis 
                  dataKey="name" 
                  type="category" 
                  width={160} 
                  stroke="hsl(var(--dark))"
                  style={{ fontFamily: 'var(--font-body)', fontWeight: 'bold' }}
                  tick={{ fontSize: 12 }} 
                />
                <Tooltip 
                  contentStyle={{
                    border: '3px solid hsl(var(--dark))',
                    borderRadius: 0,
                    boxShadow: '4px 4px 0px hsl(var(--dark))',
                    fontFamily: 'var(--font-body)',
                    fontWeight: 'bold'
                  }}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="bg-card border-[3px] border-dark p-3 shadow-[4px_4px_0px_0px] shadow-dark font-body">
                          <p className="font-bold">{data.fullName}</p>
                          <p>{data.value} risposte ({data.percentage}%)</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar 
                  dataKey="value" 
                  stroke="hsl(var(--dark))"
                  strokeWidth={3}
                >
                  {trainingNeedsData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            {/* Legend with percentages */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-4 border-t-[3px] border-dark">
              {trainingNeedsData.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div 
                    className="w-4 h-4 border-[2px] border-dark" 
                    style={{ backgroundColor: item.fill }} 
                  />
                  <span className="font-body">
                    <span className="font-bold">{item.percentage}%</span>
                    <span className="text-muted-foreground ml-1">{item.name}</span>
                  </span>
                </div>
              ))}
            </div>
          </BrutalCard>
        )}

        {/* Barra ricerca e filtri */}
        <BrutalCard className="mb-6">
          <div className="flex gap-4 items-center">
            <div className="flex-1">
              <BrutalInput
                placeholder={t('screening.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            {/* Filtro stato */}
            <select
              className="border-[3px] border-dark p-3 font-body text-base bg-card text-foreground focus:outline-none focus:ring-4 focus:ring-primary transition-all"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">{t('screening.filterAllStatus')}</option>
              <option value="pending">{t('screening.filterPending')}</option>
              <option value="approved">{t('screening.filterApproved')}</option>
              <option value="rejected">{t('screening.filterRejected')}</option>
            </select>
          </div>
        </BrutalCard>

        {filteredApplications.length === 0 ? (
          <BrutalCard className="text-center py-12">
            <CheckCircle className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <p className="text-xl font-heading font-bold mb-2">
              {t('screening.noApplicationsFound')}
            </p>
            <p className="text-muted-foreground">
              {searchTerm || statusFilter 
                ? t('screening.changeFilters')
                : t('screening.noApplicationsYet')
              }
            </p>
          </BrutalCard>
        ) : (
          <BrutalCard>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface border-b-[3px] border-dark">
                  <tr>
                    <th className="px-6 py-4 text-left font-heading font-bold">{t('common.email')}</th>
                    <th className="px-6 py-4 text-left font-heading font-bold">{t('screening.score')}</th>
                    <th className="px-6 py-4 text-left font-heading font-bold">{t('screening.submittedAt')}</th>
                    <th className="px-6 py-4 text-left font-heading font-bold">{t('screening.statusLabel')}</th>
                    <th className="px-6 py-4 text-left font-heading font-bold">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApplications.map((app) => (
                    <tr key={app.id} className="border-b-[3px] border-dark hover:bg-surface">
                      <td className="px-6 py-4">{app.user_email || app.user_id}</td>
                      <td className="px-6 py-4">
                        {getScoreBadge(app.score, app.badge_tier)}
                      </td>
                      <td className="px-6 py-4">
                        {new Date(app.submitted_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">{getStatusBadge(app.status)}</td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <TooltipProvider>
                            <UITooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => handleViewDetails(app)}
                                  className="p-2 border-[3px] border-dark bg-card shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px] hover:shadow-dark transition-all"
                                  aria-label={t('screening.viewDetails')}
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{t('screening.viewDetails')}</p>
                              </TooltipContent>
                            </UITooltip>
                          </TooltipProvider>
                          {app.status === 'pending' && (
                            <TooltipProvider>
                              <UITooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={() => handleApprove(app)}
                                    className="p-2 border-[3px] border-dark bg-primary shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px] hover:shadow-dark transition-all"
                                    aria-label={t('common.approve')}
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{t('common.approve')}</p>
                                </TooltipContent>
                              </UITooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </BrutalCard>
        )}

        {/* Details Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto border-[3px] border-dark shadow-[8px_8px_0px_#101322]">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl font-bold flex items-center gap-2">
                <Eye className="w-6 h-6 text-primary" />
                {t('screening.applicationDetails')}
              </DialogTitle>
              <DialogDescription className="text-base">
                {t('screening.score')}: {selectedApp?.score && getScoreBadge(selectedApp.score, selectedApp.badge_tier)}
              </DialogDescription>
            </DialogHeader>

            {selectedApp && (
              <div className="space-y-6 mt-4">
                <div>
                  <h3 className="font-heading font-bold text-lg mb-3 uppercase">{t('screening.answers')}</h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {Object.entries(selectedApp.answers || {}).map(([qId, answer]: [string, any]) => {
                      const question = screeningQuestions.it.find((q) => q.id === qId);
                      const option = question?.options.find((o) => o.value === answer);
                      return (
                        <div key={qId} className="p-3 bg-surface border-[2px] border-dark">
                          <p className="font-bold text-sm">{question?.text}</p>
                          <p className="text-sm text-muted-foreground">
                            {option?.label} ({option?.points} {t('screening.points')})
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes" className="font-heading font-bold uppercase">{t('screening.adminNotes')}</Label>
                  <BrutalTextarea
                    id="notes"
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    rows={3}
                  />
                </div>

                {selectedApp.status === 'pending' && (
                  <div className="space-y-2">
                    <Label htmlFor="rejection" className="font-heading font-bold uppercase">{t('screening.rejectionReason')}</Label>
                    <BrutalTextarea
                      id="rejection"
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      rows={3}
                    />
                  </div>
                )}
              </div>
            )}

            <DialogFooter className="flex gap-3 pt-4 mt-4 border-t-[3px] border-dark">
              <BrutalButton variant="outline" onClick={() => setShowDialog(false)}>
                {t('common.close')}
              </BrutalButton>
              {selectedApp?.status === 'pending' && (
                <>
                  <BrutalButton
                    variant="danger"
                    onClick={() => handleReject(selectedApp)}
                    disabled={processing}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    {t('common.reject')}
                  </BrutalButton>
                  <BrutalButton onClick={() => handleApprove(selectedApp)} disabled={processing}>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {t('common.approve')}
                  </BrutalButton>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default ScreeningAdmin;
